import Decimal from "decimal.js";

/**
 * Autopilot (automatic-streak) completion-time math.
 *
 * Single source of truth shared by the Hub estimate and the Solo CMS simulation
 * panel. Keep the two constants below in sync with the backend's autopilot
 * cycle-safe-margin and the Hub's balance safe-margin.
 */

/** Worst-case runway reserved per buy→sell cycle. Mirrors backend CYCLE_SAFE_MARGIN_MS. */
export const CYCLE_SAFE_MARGIN_MS = 290_000;
const MS_PER_MINUTE = 60_000;
/** Usable share of balance after the slippage+fees headroom (10%). */
export const BALANCE_USABLE_RATIO = 0.9;

/** Per-trade size the autopilot uses: the config max, capped by usable balance. */
export function effectiveTradeSize(maxTradeSize: number, balance: number): number {
  return Decimal.min(maxTradeSize, new Decimal(balance).mul(BALANCE_USABLE_RATIO)).toNumber();
}

/** Balance at which trades stop being balance-capped and hit the config max. */
export function fullyFundedBalance(maxTradeSize: number): number {
  return new Decimal(maxTradeSize).div(BALANCE_USABLE_RATIO).toNumber();
}

/**
 * Volume one buy+sell cycle produces at the average randomized trade size.
 * = (minTradeSize × 2 + tradeSize × 2) / 2 — the ×2 (buy+sell) and ÷2
 * (min↔max average) cancel, leaving minTradeSize + tradeSize.
 */
export function avgCycleVolume(minTradeSize: number, tradeSize: number): number {
  return new Decimal(minTradeSize).plus(tradeSize).toNumber();
}

export function estimateCycles(targetVolume: number, minTradeSize: number, tradeSize: number): number {
  const perCycle = new Decimal(minTradeSize).plus(tradeSize);
  if (targetVolume <= 0 || perCycle.lessThanOrEqualTo(0)) return 0;
  return new Decimal(targetVolume).div(perCycle).ceil().toNumber();
}

export function estimateNeededMinutes(targetVolume: number, minTradeSize: number, tradeSize: number): number {
  const cycles = estimateCycles(targetVolume, minTradeSize, tradeSize);
  return new Decimal(cycles).mul(CYCLE_SAFE_MARGIN_MS).div(MS_PER_MINUTE).ceil().toNumber();
}

/** "5h 10m", "58m", "1d 4h" — compact, whole units. */
export function formatAutopilotDuration(totalMinutes: number): string {
  if (totalMinutes <= 0) return "0m";
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(" ");
}
